import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ChefHat, Loader2, Edit3, Sparkles, Search, X, ArrowUpDown, Filter,
  MoreVertical, Archive, ArchiveRestore, Trash2, ArrowLeft,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import api from "../../lib/api";

/**
 * Tab "Elaboraciones Base" — fichas técnicas reutilizables.
 *
 * Diseño (Feb 2026):
 *  - Grid de cards con 3 KPI chips: Rendimiento (gris), Caducidad (color por
 *    urgencia: ≤1d rojo, ≤3d ámbar, ≤7d verde, sin / >7d gris), Usada en N
 *    platos (azul). Notas en cursiva + contador de ingredientes al pie.
 *  - Menú "···" con: Editar / Archivar (o Restaurar / Borrar definitivamente
 *    en la vista de archivadas).
 *  - Toggle "Ver archivadas" arriba a la derecha. Lifecycle de soft-delete:
 *    archivar → toast Gmail-style con Deshacer 5s → vista archivadas →
 *    borrar definitivamente con confirm.
 */

// ── Caducidad: color rule (user-confirmed) ────────────────────────────
//   ≤ 1d → rojo, ≤ 3d → ámbar, ≤ 7d → verde, > 7d / sin → gris
function expiryTone(days) {
  if (!days && days !== 0) return { bg: "bg-muted", text: "text-muted-foreground", border: "border-transparent" };
  if (days <= 1)  return { bg: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-100" };
  if (days <= 3)  return { bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-100" };
  if (days <= 7)  return { bg: "bg-emerald-50",text: "text-emerald-700",border: "border-emerald-100" };
  return { bg: "bg-muted", text: "text-muted-foreground", border: "border-transparent" };
}

function KpiChip({ label, value, tone = "default" }) {
  const tones = {
    default: { bg: "bg-muted/60",   text: "text-foreground",       label: "text-muted-foreground" },
    blue:    { bg: "bg-blue-50",    text: "text-blue-700",          label: "text-blue-500" },
    expiry:  null, // handled inline by expiryTone()
  };
  const t = tones[tone] || tones.default;
  return (
    <div className={`flex-1 min-w-0 rounded-lg ${t.bg} px-2.5 py-2 text-center`}>
      <div className={`text-[10px] font-medium uppercase tracking-wide ${t.label}`}>{label}</div>
      <div className={`text-sm font-bold ${t.text} truncate mt-0.5`} title={String(value)}>{value}</div>
    </div>
  );
}

function ExpiryChip({ days }) {
  const t = expiryTone(days);
  const value = days || days === 0 ? `${days}d` : "—";
  return (
    <div className={`flex-1 min-w-0 rounded-lg ${t.bg} px-2.5 py-2 text-center border ${t.border}`}>
      <div className={`text-[10px] font-medium uppercase tracking-wide ${t.text} opacity-70`}>Caducidad</div>
      <div className={`text-sm font-bold ${t.text} truncate mt-0.5`}>{value}</div>
    </div>
  );
}

export default function RecipesBaseTab({ restaurantId, reloadKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usedByMap, setUsedByMap] = useState({});
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("az");
  const [filterExpiry, setFilterExpiry] = useState("all");
  const [viewArchived, setViewArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null); // { batch_recipe_id, name }
  const [busyId, setBusyId] = useState(null);

  // Independent fetcher for the "archived count" badge — kept in sync even
  // when we're on the active view.
  const fetchArchivedCount = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const r = await api.listBatchRecipes(restaurantId, { archived: true });
      setArchivedCount((r?.batch_recipes || []).length);
    } catch { /* silent */ }
  }, [restaurantId]);

  const fetchItems = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.listBatchRecipes(restaurantId, { archived: viewArchived });
      const list = r?.batch_recipes || [];
      setItems(list);
      // Used-by counts only relevant in active view (archived ones don't
      // need the usage drill-down — they're already out of escandallos).
      if (!viewArchived) {
        const entries = await Promise.all(
          list.slice(0, 50).map(async (b) => {
            try {
              const u = await api.request(`/restaurants/${restaurantId}/batch-recipes/${b.batch_recipe_id}/used-by`);
              return [b.batch_recipe_id, u?.used_by || []];
            } catch {
              return [b.batch_recipe_id, []];
            }
          }),
        );
        setUsedByMap(Object.fromEntries(entries));
      } else {
        setUsedByMap({});
      }
    } catch (e) {
      toast.error(e?.message || "Error cargando elaboraciones base");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, viewArchived]);

  useEffect(() => { fetchItems(); }, [fetchItems, reloadKey]);
  useEffect(() => { fetchArchivedCount(); }, [fetchArchivedCount, reloadKey, viewArchived]);

  const handleEdit = (b) => setEditing({
    batch_recipe_id: b.batch_recipe_id,
    name: b.name || "",
    yield_qty: b.yield_qty ?? 1,
    yield_unit: b.yield_unit || "ud",
    shelf_life_days: b.shelf_life_days ?? "",
    preparation_notes: b.preparation_notes || "",
    ingredients: b.ingredients || [],
  });

  const saveEdit = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    if (!name) { toast.error("El nombre es obligatorio"); return; }
    setSavingEdit(true);
    try {
      await api.updateBatchRecipe(restaurantId, editing.batch_recipe_id, {
        name,
        yield_qty: Number(editing.yield_qty) || 1,
        yield_unit: editing.yield_unit || "ud",
        shelf_life_days: editing.shelf_life_days === "" ? null : Number(editing.shelf_life_days),
        preparation_notes: editing.preparation_notes || null,
        ingredients: editing.ingredients || [],
      });
      toast.success("Elaboración base actualizada");
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error(e?.message || "Error guardando");
    } finally { setSavingEdit(false); }
  };

  // ── Archive (active → archived) with Gmail-style undo toast ──────────
  const handleArchive = async (b) => {
    setBusyId(b.batch_recipe_id);
    try {
      await api.archiveBatchRecipe(restaurantId, b.batch_recipe_id);
      // Optimistically remove from the active list immediately so the user
      // sees the change without waiting for the refetch.
      setItems((prev) => prev.filter((x) => x.batch_recipe_id !== b.batch_recipe_id));
      setArchivedCount((c) => c + 1);
      toast.success(`"${b.name}" archivada`, {
        description: "Se moverá a Archivadas. Puedes restaurarla en cualquier momento.",
        duration: 5000,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await api.restoreBatchRecipe(restaurantId, b.batch_recipe_id);
              toast.success("Restaurada");
              fetchItems();
              fetchArchivedCount();
            } catch (e) {
              toast.error(e?.message || "No se pudo deshacer");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.message || "Error al archivar");
      fetchItems();
    } finally { setBusyId(null); }
  };

  const handleUnarchive = async (b) => {
    setBusyId(b.batch_recipe_id);
    try {
      await api.restoreBatchRecipe(restaurantId, b.batch_recipe_id);
      setItems((prev) => prev.filter((x) => x.batch_recipe_id !== b.batch_recipe_id));
      setArchivedCount((c) => Math.max(0, c - 1));
      toast.success(`"${b.name}" restaurada`);
    } catch (e) {
      toast.error(e?.message || "Error al restaurar");
      fetchItems();
    } finally { setBusyId(null); }
  };

  const handlePermanentDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.batch_recipe_id);
    try {
      await api.deleteBatchRecipe(restaurantId, confirmDelete.batch_recipe_id);
      toast.success("Elaboración borrada definitivamente");
      setItems((prev) => prev.filter((x) => x.batch_recipe_id !== confirmDelete.batch_recipe_id));
      setArchivedCount((c) => Math.max(0, c - 1));
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e?.message || "Error borrando");
    } finally { setBusyId(null); }
  };

  const filteredItems = useMemo(() => {
    let list = [...items];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((b) =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.preparation_notes || "").toLowerCase().includes(q),
      );
    }

    if (filterExpiry !== "all") {
      list = list.filter((b) => {
        const d = b.shelf_life_days;
        if (filterExpiry === "none")   return !d;
        if (filterExpiry === "lte3")   return d && d <= 3;
        if (filterExpiry === "lte7")   return d && d <= 7;
        if (filterExpiry === "lte30")  return d && d <= 30;
        if (filterExpiry === "gt30")   return d && d > 30;
        return true;
      });
    }

    list.sort((a, b) => {
      if (sortBy === "az")    return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "za")    return (b.name || "").localeCompare(a.name || "");
      if (sortBy === "used")  return ((usedByMap[b.batch_recipe_id] || []).length) - ((usedByMap[a.batch_recipe_id] || []).length);
      if (sortBy === "ing")   return (b.ingredients?.length || 0) - (a.ingredients?.length || 0);
      return 0;
    });

    return list;
  }, [items, searchQuery, sortBy, filterExpiry, usedByMap]);

  // ── Toggle archived / active view button ──────────────────────────────
  const ToggleArchivedBtn = (
    <Button
      variant={viewArchived ? "default" : "outline"}
      size="sm"
      onClick={() => setViewArchived((v) => !v)}
      data-testid="base-toggle-archived"
      className="gap-1.5"
    >
      {viewArchived ? <ArrowLeft className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
      {viewArchived ? "Volver a activas" : "Ver archivadas"}
      {!viewArchived && archivedCount > 0 && (
        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-muted text-[10px] font-bold">
          {archivedCount}
        </span>
      )}
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando elaboraciones…
      </div>
    );
  }

  // ── Empty state — active view ─────────────────────────────────────────
  if (!viewArchived && !items.length) {
    return (
      <div className="space-y-4" data-testid="recipes-base-tab">
        <div className="flex justify-end">{ToggleArchivedBtn}</div>
        <Card className="border-dashed bg-muted/30">
          <CardContent className="py-16 text-center space-y-3">
            <div className="w-14 h-14 mx-auto rounded-full bg-violet-100 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Todavía no tienes elaboraciones base</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Cuando generes una receta nueva con IA (por ejemplo <em>Lasaña boloñesa</em>),
                detectaremos automáticamente las salsas, fondos o cremas que en cocina
                profesional se preparan aparte y las guardaremos aquí como elaboraciones
                reutilizables.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recipes-base-tab">

      {/* ── Top bar: search + sort + expiry filter + archived toggle ───── */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-end">
          {ToggleArchivedBtn}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={viewArchived ? "Buscar archivadas…" : "Buscar elaboraciones base…"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 h-10 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="base-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-48 h-10 gap-1.5" data-testid="base-sort-select">
              <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="az">A-Z (Alfabético)</SelectItem>
              <SelectItem value="za">Z-A (Inverso)</SelectItem>
              {!viewArchived && <SelectItem value="used">Más usadas</SelectItem>}
              <SelectItem value="ing">Más ingredientes</SelectItem>
            </SelectContent>
          </Select>

          {!viewArchived && (
            <Select value={filterExpiry} onValueChange={setFilterExpiry}>
              <SelectTrigger className="w-full sm:w-52 h-10 gap-1.5" data-testid="base-expiry-select">
                <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los días de caducidad</SelectItem>
                <SelectItem value="none">Sin caducidad definida</SelectItem>
                <SelectItem value="lte3">≤ 3 días</SelectItem>
                <SelectItem value="lte7">≤ 7 días</SelectItem>
                <SelectItem value="lte30">≤ 30 días</SelectItem>
                <SelectItem value="gt30">Más de 30 días</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {viewArchived && (
          <span className="inline-flex items-center gap-1.5 mr-2 text-foreground font-medium">
            <Archive className="w-3.5 h-3.5" /> Archivadas:
          </span>
        )}
        {filteredItems.length === items.length
          ? `${items.length} elaboración${items.length === 1 ? "" : "es"}`
          : `${filteredItems.length} de ${items.length} elaboración${items.length === 1 ? "" : "es"}`}
      </p>

      {filteredItems.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="base-empty-archived">
          {viewArchived
            ? "No hay elaboraciones archivadas. Las que archives aparecerán aquí."
            : "Sin resultados para los filtros aplicados."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredItems.map((b) => {
            const usedBy = usedByMap[b.batch_recipe_id] || [];
            const usedCount = usedBy.length;
            const ingCount = (b.ingredients || []).length;
            const isBusy = busyId === b.batch_recipe_id;
            return (
              <Card
                key={b.batch_recipe_id}
                data-testid={`base-card-${b.batch_recipe_id}`}
                className={`relative transition-shadow hover:shadow-md ${isBusy ? "opacity-50 pointer-events-none" : ""}`}
              >
                <CardContent className="p-5 space-y-4">
                  {/* Header — title + ··· menu */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4
                        className="font-heading font-semibold text-lg leading-tight truncate flex items-center gap-1.5"
                        title={b.name}
                      >
                        <ChefHat className="w-4 h-4 text-violet-500 flex-shrink-0" />
                        {b.name}
                      </h4>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost" size="icon"
                          className="h-8 w-8 -mr-2 -mt-1 text-muted-foreground hover:text-foreground"
                          data-testid={`base-menu-${b.batch_recipe_id}`}
                          aria-label="Acciones"
                        >
                          {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {viewArchived ? (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleUnarchive(b)}
                              data-testid={`base-restore-${b.batch_recipe_id}`}
                            >
                              <ArchiveRestore className="w-4 h-4 mr-2" />
                              Restaurar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-rose-700 focus:text-rose-700 focus:bg-rose-50"
                              onClick={() => setConfirmDelete({ batch_recipe_id: b.batch_recipe_id, name: b.name })}
                              data-testid={`base-delete-perm-${b.batch_recipe_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Borrar definitivamente
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleEdit(b)}
                              data-testid={`base-edit-${b.batch_recipe_id}`}
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-amber-700 focus:text-amber-800 focus:bg-amber-50"
                              onClick={() => handleArchive(b)}
                              data-testid={`base-archive-${b.batch_recipe_id}`}
                            >
                              <Archive className="w-4 h-4 mr-2" />
                              Archivar
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* KPI chips — Rendimiento · Caducidad · Usada en N platos */}
                  <div className="flex items-stretch gap-2">
                    <KpiChip
                      label="Rendimiento"
                      value={`${b.yield_qty}${b.yield_unit || ""}`}
                    />
                    <ExpiryChip days={b.shelf_life_days} />
                    <KpiChip
                      label="Usada en"
                      value={`${usedCount} plato${usedCount === 1 ? "" : "s"}`}
                      tone="blue"
                    />
                  </div>

                  {/* Notes (italic) */}
                  {b.preparation_notes && (
                    <p
                      className="text-sm italic text-muted-foreground line-clamp-2"
                      title={b.preparation_notes}
                    >
                      {b.preparation_notes}
                    </p>
                  )}

                  {/* Footer — ingredient count */}
                  <div className="text-xs text-muted-foreground">
                    {ingCount} ingrediente{ingCount === 1 ? "" : "s"}
                    {viewArchived && b.archived_at && (
                      <span className="ml-2 opacity-70">
                        · Archivada el {new Date(b.archived_at).toLocaleDateString("es-ES")}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Edit dialog ─────────────────────────────────────────────────── */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="base-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar elaboración base</DialogTitle>
            <DialogDescription>
              Cambia el nombre, rendimiento o notas de preparación. Los ingredientes se editan desde inventario.
            </DialogDescription>
          </DialogHeader>
          {editing && (() => {
            const usedBy = usedByMap[editing.batch_recipe_id] || [];
            return (
              <div className="space-y-4">
                {usedBy.length > 0 && (
                  <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                    Esta elaboración base se usa en <strong>{usedBy.length} plato{usedBy.length === 1 ? "" : "s"}</strong>.
                    Los cambios afectarán al coste y escandallo de todos ellos.
                    <div className="text-xs mt-1 opacity-80">{usedBy.slice(0, 4).map((u) => u.dish_name).join(" · ")}{usedBy.length > 4 ? "…" : ""}</div>
                  </div>
                )}
                <div>
                  <Label>Nombre</Label>
                  <Input value={editing.name}
                    onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                    data-testid="base-edit-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Rendimiento (cantidad)</Label>
                    <Input type="number" min="0.01" step="0.01" value={editing.yield_qty}
                      onChange={(e) => setEditing((p) => ({ ...p, yield_qty: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Unidad</Label>
                    <Input value={editing.yield_unit}
                      onChange={(e) => setEditing((p) => ({ ...p, yield_unit: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label>Caducidad (días) — opcional</Label>
                  <Input type="number" min="0" value={editing.shelf_life_days}
                    onChange={(e) => setEditing((p) => ({ ...p, shelf_life_days: e.target.value }))} />
                </div>
                <div>
                  <Label>Notas de preparación</Label>
                  <Textarea rows={4} value={editing.preparation_notes}
                    onChange={(e) => setEditing((p) => ({ ...p, preparation_notes: e.target.value }))} />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="base-edit-save">
              {savingEdit && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Permanent delete confirmation (archived view only) ──────────── */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="base-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Borrar definitivamente</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a borrar <strong>{confirmDelete?.name}</strong> de forma permanente.
              Esta acción no se puede deshacer y el registro desaparecerá del histórico.
              {" "}Si la elaboración aún está vinculada a algún plato, te bloquearemos el borrado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="base-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              onClick={handlePermanentDelete}
              data-testid="base-delete-confirm-action"
            >
              Borrar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
